/**
 * DashboardUi.js
 * Shared design tokens, icons and UI primitives for the Integrity Dashboard.
 * Mirrors the visual language of Dynamicgraph.jsx (navy / amber / slate, rounded cards).
 */
import React from "react";

/* ------------------------------------------------------------------ */
/*  Design tokens (same as Dynamicgraph)                               */
/* ------------------------------------------------------------------ */
export const BRAND = {
  navy: "#154A79",
  amber: "#FEB13F",
  green: "#1B8A5A",
  red: "#D64545",
  sky: "#7FB3D5",
};

/** Donut palette – brand colours first, then harmonious extras */
export const CHART_COLORS = [
  "#154A79",
  "#FEB13F",
  "#7FB3D5",
  "#1B8A5A",
  "#8E7CC3",
  "#F28B6B",
  "#4FB3BF",
  "#A3A7B0",
];

/* ------------------------------------------------------------------ */
/*  Icons – lightweight inline SVGs                                     */
/* ------------------------------------------------------------------ */
const Icon = ({ children, size = 16, className = "", ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    {...rest}
  >
    {children}
  </svg>
);

export const IconDownload = (p) => (
  <Icon {...p}>
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </Icon>
);
export const IconInfo = (p) => (
  <Icon strokeWidth="2.3" {...p}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </Icon>
);
export const IconCheckCircle = (p) => (
  <Icon strokeWidth="2.3" {...p}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <path d="m9 11 3 3L22 4" />
  </Icon>
);
export const IconXCircle = (p) => (
  <Icon strokeWidth="2.3" {...p}>
    <circle cx="12" cy="12" r="10" />
    <path d="m15 9-6 6" />
    <path d="m9 9 6 6" />
  </Icon>
);
export const IconBarChart = (p) => (
  <Icon {...p}>
    <path d="M3 3v18h18" />
    <path d="M7 16v-4" />
    <path d="M12 16V8" />
    <path d="M17 16v-7" />
  </Icon>
);
export const IconPieChart = (p) => (
  <Icon {...p}>
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
    <path d="M22 12A10 10 0 0 0 12 2v10z" />
  </Icon>
);
export const IconTable = (p) => (
  <Icon {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M9 4v16" />
  </Icon>
);
export const IconImage = (p) => (
  <Icon {...p}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
  </Icon>
);
export const IconHash = (p) => (
  <Icon {...p}>
    <path d="M4 9h16" />
    <path d="M4 15h16" />
    <path d="M10 3 8 21" />
    <path d="M16 3l-2 18" />
  </Icon>
);
export const IconCheckSquare = (p) => (
  <Icon {...p}>
    <path d="m9 11 3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </Icon>
);
export const IconFileText = (p) => (
  <Icon {...p}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
    <path d="M10 9H8" />
  </Icon>
);
export const IconAlertTriangle = (p) => (
  <Icon {...p}>
    <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </Icon>
);
export const IconInbox = (p) => (
  <Icon {...p}>
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
  </Icon>
);
export const IconSearch = (p) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </Icon>
);
export const IconChevronDown = (p) => (
  <Icon strokeWidth="2.3" {...p}>
    <path d="m6 9 6 6 6-6" />
  </Icon>
);
export const IconLayers = (p) => (
  <Icon {...p}>
    <path d="m12 2 9 5-9 5-9-5 9-5Z" />
    <path d="m3 12 9 5 9-5" />
    <path d="m3 17 9 5 9-5" />
  </Icon>
);

/* ------------------------------------------------------------------ */
/*  UI primitives                                                       */
/* ------------------------------------------------------------------ */

/** Card shell with icon chip, title, optional subtitle and right-side action */
export const SectionCard = ({
  icon,
  title,
  subtitle,
  action,
  className = "",
  children,
}) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md ${className}`}
  >
    <div className="mb-4 flex items-center gap-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#154A79]/[0.06] text-[#154A79]">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className="truncate text-sm font-semibold text-[#154A79]"
          title={title}
        >
          {title}
        </p>
        {subtitle && (
          <p className="truncate text-xs text-slate-400">{subtitle}</p>
        )}
      </div>
      {action}
    </div>
    {children}
  </div>
);

/** Collapsible page section with amber accent bar + count pill */
export const Section = ({
  id,
  title,
  description,
  count,
  countLabel = "checks",
  collapsed,
  onToggle,
  scrollMargin = 0,
  children,
}) => (
  <section
    id={id}
    style={{ scrollMarginTop: scrollMargin }}
    className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8"
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      className="flex w-full items-center gap-3 text-left"
    >
      <span className="h-8 w-1.5 shrink-0 rounded-full bg-[#FEB13F]" />
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-xl font-semibold text-[#154A79]">
          {title}
        </h3>
        {description && (
          <p className="mt-0.5 text-xs text-slate-500 md:text-sm">
            {description}
          </p>
        )}
      </div>
      {count !== undefined && (
        <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">
          {count} {countLabel}
        </span>
      )}
      <IconChevronDown
        size={18}
        className={`shrink-0 text-slate-400 transition-transform ${
          collapsed ? "-rotate-90" : ""
        }`}
      />
    </button>
    {!collapsed && <div className="mt-6">{children}</div>}
  </section>
);

/** KPI card shown at the top of the dashboard */
export const StatCard = ({ icon, label, value, hint, tone = "default" }) => {
  const tones = {
    default: "text-[#154A79] bg-[#154A79]/[0.06]",
    warn: "text-[#D64545] bg-[#FDEDED]",
    good: "text-[#1B8A5A] bg-[#EAF7F1]",
    amber: "text-[#B77600] bg-[#FFF4E1]",
  };
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#FEB13F]/60 hover:shadow-md">
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-slate-400">{label}</p>
        <p className="text-2xl font-bold leading-tight text-slate-800">
          {value}
        </p>
        {hint && <p className="truncate text-[11px] text-slate-400">{hint}</p>}
      </div>
    </div>
  );
};

/** Green / red pill used in tables */
export const StatusBadge = ({ isPass, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
      isPass ? "bg-[#EAF7F1] text-[#1B8A5A]" : "bg-[#FDEDED] text-[#D64545]"
    }`}
  >
    {isPass ? <IconCheckCircle size={12} /> : <IconXCircle size={12} />}
    {label}
  </span>
);

/** Slim pass / fail proportion bar */
export const SplitBar = ({ passed, failed }) => {
  const total = passed + failed;
  const pct = total ? (passed / total) * 100 : 0;
  return (
    <div
      className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100"
      role="img"
      aria-label={`${passed} passed, ${failed} failed`}
    >
      {total > 0 && (
        <>
          <div className="bg-[#1B8A5A]" style={{ width: `${pct}%` }} />
          <div className="bg-[#D64545]" style={{ width: `${100 - pct}%` }} />
        </>
      )}
    </div>
  );
};

/** Dashed placeholder for charts / blocks without data */
export const ChartEmptyState = ({
  title = "No data available",
  message = "There isn't enough data to render this yet.",
  height = 240,
}) => (
  <div
    style={{ minHeight: height }}
    className="flex w-full flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-6 text-center"
  >
    <div className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-300">
      <IconBarChart size={20} />
    </div>
    <div>
      <p className="text-sm font-semibold text-slate-500">{title}</p>
      <p className="mx-auto mt-1 max-w-[260px] text-xs leading-relaxed text-slate-400">
        {message}
      </p>
    </div>
  </div>
);
